import { Controller } from '@hotwired/stimulus'

// Touch-first PIN pad for the clock-in kiosk. Digits tapped on the keypad
// accumulate into the hidden `input`; the `dots` reflect how many are entered;
// the form auto-submits once `length` digits are reached (the employee PIN is
// fixed-length, so there's no separate confirm button). Physical-keyboard
// number keys work too, for kiosks with one attached.
export class PinPadController extends Controller {
  static targets = ['input', 'dots', 'form']
  static values = { length: { type: Number, default: 4 } }

  connect () {
    this.value = ''
    this.render()
    this.onKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.onKeydown)
  }

  disconnect () {
    document.removeEventListener('keydown', this.onKeydown)
  }

  press (event) {
    this.append(event.currentTarget.dataset.digit)
  }

  append (digit) {
    if (this.value.length >= this.lengthValue) return

    this.value += digit
    this.render()
    if (this.value.length === this.lengthValue) this.submit()
  }

  backspace () {
    this.value = this.value.slice(0, -1)
    this.render()
  }

  clear () {
    this.value = ''
    this.render()
  }

  submit () {
    // Dim + lock the pad so a completed PIN reads as "checking…" rather than a
    // dead tap while the Turbo request flies (the modal is swapped on response).
    this.element.classList.add('opacity-60', 'pointer-events-none')
    // Let the last dot paint before the Turbo submit swaps the modal.
    requestAnimationFrame(() => this.formTarget.requestSubmit())
  }

  render () {
    this.inputTarget.value = this.value
    this.dotTargets.forEach((dot, index) => {
      dot.classList.toggle('bg-primary', index < this.value.length)
      dot.classList.toggle('border-primary', index < this.value.length)
    })
    // Announce progress for assistive tech (the dots are decorative spans).
    this.dotsTarget.setAttribute('aria-label', `${this.value.length} / ${this.lengthValue}`)
  }

  get dotTargets () {
    return Array.from(this.dotsTarget.children)
  }

  handleKeydown (event) {
    if (event.key >= '0' && event.key <= '9') {
      this.append(event.key)
    } else if (event.key === 'Backspace') {
      event.preventDefault()
      this.backspace()
    }
  }
}
